import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, LogIn, ArrowRight, KeyRound, ShieldCheck, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

/**
 * Customer sign-in — passwordless: email + one-time code (real, via Resend).
 * No more demo OTP boxes. If the email doesn't exist yet the backend silently
 * creates a customer record on first verified sign-in.
 */
const Login = () => {
  const { startEmailOtp, verifyOtp, signInAfterOtp, clearPendingOtp } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { toast } = useToast();
  const from = loc.state?.from || '/dashboard';

  const [step, setStep] = useState('email'); // email | otp
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    if (!email) return toast({ title: 'Please enter your email' });
    setBusy(true);
    const r = await startEmailOtp(email, 'login');
    setBusy(false);
    if (!r.ok) return toast({ title: r.error });
    toast({ title: 'Code sent', description: `Check ${email} for your 6-digit code.` });
    setStep('otp');
  };

  const verify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast({ title: 'Enter the full 6-digit code' });
    setBusy(true);
    const v = await verifyOtp(otp);
    setBusy(false);
    if (!v.ok) return toast({ title: v.error });
    signInAfterOtp(v.email, v.token, v.name);
    toast({ title: 'Welcome back!' });
    nav(from, { replace: true });
  };

  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-10 items-center">
          <div className="hidden lg:block">
            <div className="inline-flex items-center gap-2 bg-white border border-[#1F0068]/10 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#1F0068] mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C39]" /> My Account
            </div>
            <h1 className="text-5xl font-bold text-[#1F0068] tracking-tight leading-[1.05]">
              Welcome back to <br /><span className="text-[#FF5C39]">Domestic Home</span>
            </h1>
            <p className="mt-5 text-lg text-[#1F0068]/70 max-w-md">
              Sign in with just your email — we’ll send you a one-time code so you never have to remember a password.
            </p>
            <div className="mt-8 space-y-3">
              {[
                'Book a repair in under 60 seconds',
                'Track your engineer in real time',
                'Make and follow up on complaints',
                'Manage all your plans in one place',
              ].map((t) => (
                <div key={t} className="flex items-center gap-3 text-[#1F0068]">
                  <span className="w-6 h-6 rounded-full bg-[#00b67a]/15 text-[#00b67a] flex items-center justify-center text-xs font-bold">✓</span>
                  <span className="text-sm font-medium">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 md:p-10 border border-[#1F0068]/5 shadow-xl">
            {step === 'email' && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><LogIn className="w-5 h-5" /></div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1F0068]">Sign in</h2>
                    <p className="text-sm text-[#1F0068]/60">Enter your email and we’ll send you a one-time code.</p>
                  </div>
                </div>
                <form onSubmit={sendCode} className="space-y-4">
                  <div>
                    <Label className="text-[#1F0068] font-semibold">Email</Label>
                    <div className="relative mt-2">
                      <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" />
                      <Input data-testid="customer-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com" className="h-12 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                    </div>
                  </div>
                  <Button data-testid="customer-send-code" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send code <ArrowRight className="w-4 h-4 ml-1" /></>}
                  </Button>
                  <div className="text-center text-sm text-[#1F0068]/70 pt-2">
                    New to us? <Link to="/register" className="font-bold text-[#FF5C39] hover:underline">Create an account</Link>
                  </div>
                </form>
              </>
            )}

            {step === 'otp' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><KeyRound className="w-5 h-5" /></div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1F0068]">Enter your code</h2>
                    <p className="text-sm text-[#1F0068]/60">Sent to <b>{email}</b></p>
                  </div>
                </div>
                <div className="bg-[#FFF6EB] rounded-xl p-3 text-xs text-[#1F0068]/70 mb-4">
                  Please check your inbox (and spam folder). The code expires in 10 minutes.
                </div>
                <form onSubmit={verify}>
                  <Input data-testid="customer-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))} maxLength={6} placeholder="6-digit code" className="h-14 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10 text-center text-2xl tracking-widest" />
                  <Button data-testid="customer-verify" type="submit" disabled={busy} className="mt-3 w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Verify & sign in <ShieldCheck className="w-4 h-4 ml-1" /></>}
                  </Button>
                  <button type="button" onClick={() => { clearPendingOtp(); setStep('email'); setOtp(''); }} className="mt-3 text-xs text-[#1F0068]/70 underline block mx-auto">Use a different email</button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Login;
